import React from 'react';
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  // InputLabel,
  makeStyles,
  TextareaAutosize
  // Typography
} from '@material-ui/core';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {APP} from 'src/style/variable';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import {settleThePatientCopay, updateGuarantorDueAsSettleStatement} from '@shared/services/DueInvoiceServices';
import {CommunicationService} from 'src/service/CommunicationService';

const confirmDialogStyle = makeStyles(() =>
  createStyles({
    title: {
      fontSize: '1.5rem',
      padding: '12px 0'
      // fontWeight: 'bold'
    },
    subtitle: {
      fontSize: '1.2rem',
      padding: '4px 0 12px'
    },
    description: {
      fontSize: '1rem',
      marginTop: '32px'
    },
    label: {
      marginTop: '0.5rem',
      marginBottom: '0.5rem'
    }
  })
);

const textArea = {
  height: 100,
  width: '100%',
  // marginBottom: '0rem',
  // marginTop: '0.5rem',
  borderColor: '#c1c1c1',
  borderRadius: '3pt',
  paddingTop: '1rem',
  paddingLeft: '0.8rem',
  opacity: 0.6,
  fontFamily: APP.FONT_FAMILY.REGULAR,
  fontSize: '1rem'
};

interface PaymentProps {
  open: boolean;
  title: string | React.ReactElement;
  subTitle: string | React.ReactElement;
  groupId: string;
  clinicId: string;
  guarantorId?: string;
  onClose?(params: 'submit' | 'cancel'): void;
  isSettleCopay?: boolean;
  selectedCopayId?: number;
}
const MakeSettleStatement: React.FC<PaymentProps> = props => {
  const {
    open,
    groupId,
    clinicId,
    guarantorId,
    title,
    subTitle,
    onClose,
    isSettleCopay,
    selectedCopayId
  } = props;
  const classes = confirmDialogStyle();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  // states
  const [loading, setLoader] = React.useState<boolean>(false);

  const {control, handleSubmit, errors} = useForm<{
    settledReason: string;
  }>({
    mode: 'all',
    defaultValues: {
      settledReason: ''
    },
    resolver: async values => {
      const {settledReason} = values;
      let error: any = {};
      if (!settledReason) {
        error = {
          ...errors,
          settledReason: { message: isSettleCopay ? 'VALIDATORS.SETTLED_COPAY_NOTES' : 'VALIDATORS.SETTLED_NOTES' }
        };
      }
      return {values, errors: error};
    }
  });

  const handleClose = (params: 'submit' | 'cancel'): void => {
    if (typeof onClose === 'function') {
      setLoader(false);
      onClose(params);
    }
  };

  const submit = async (data: any) => {
    setLoader(true);
    try {
      if (isSettleCopay && selectedCopayId) {
        // Call the different API when isSettleCopay is true
        const res = await settleThePatientCopay(groupId, clinicId, selectedCopayId, {
          settled_reason: data.settledReason
        });
        if (res.message) {
          enqueueSnackbar(res.message, SnackBarConfig('s'));
        }
      } else {
        if (guarantorId) {
          // Call the original API when isSettleCopay is false
          const res = await updateGuarantorDueAsSettleStatement(groupId, clinicId, guarantorId, {
            settled_reason: data.settledReason
          });
          if (res.message) {
            CommunicationService.sendEvent({
              action: 'GUARANTOR_STATEMENT_SETTLED',
              data: null
            });
            enqueueSnackbar(res.message, SnackBarConfig('s'));
          }
        }
      }
      handleClose('submit');
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'An error occurred', SnackBarConfig('e'));
    } finally {
      setLoader(false);
    }
  };
  return (
    <Dialog
      data-test-id="pcl-payments-settlementPopUp"
      maxWidth={'sm'}
      fullWidth
      open={open}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      onClose={(): void => {
        handleClose('cancel');
      }}
      aria-labelledby="confirm-dialog"
      aria-describedby="confirm-dialog">
      <DialogTitle disableTypography>{title}</DialogTitle>
      <DialogContent dividers>
        {subTitle && <div>{subTitle}</div>}
        {/* <InputLabel shrink required className={classes.label}>
          {t('.LABELS.REASON')}
        </InputLabel> */}
        <div
          className={classes.label}
          data-test-id="pcl-payments-settleReasonLabel">
          {t('DUE_INVOICES.SETTLE_REASON')}*
        </div>
        <FormControl
          error={!!errors?.settledReason?.message}
          fullWidth={true}
          variant="outlined">
          <Controller
            id="settledReason"
            as={
              <TextareaAutosize
                data-test-id="pcl-payments-textArea"
                rowsMax={10}
                rows={4}
                aria-label="maximum height"
                placeholder={t(
                  'DUE_INVOICES.PLACEHOLDERS.UPDATE_NOTIFICATION_REASON'
                )}
                style={{...textArea}}
              />
            }
            margin={'dense'}
            control={control}
            name={'settledReason'}
            type={'text'}
            value={'text'}
            autoComplete="on"
            error={!!errors?.settledReason?.message}
          />
          {errors?.settledReason?.message && (
            <FormHelperText>{t(errors?.settledReason?.message)}</FormHelperText>
          )}
        </FormControl>
      </DialogContent>
      <DialogActions style={{display: 'flex'}}>
        <Button
          data-test-id="pcl-payments-cancelSettlementReason"
          disabled={loading}
          onClick={() => {
            handleClose('cancel');
          }}
          className={['button'].join(' ')}>
          {loading ? <ButtonLoader /> : t('COMMON.CANCEL')}
        </Button>
        <Button
          disabled={loading}
          variant="contained"
          type="submit"
          color="primary"
          data-test-id="pcl-payments-confirmSettlement"
          onClick={handleSubmit(submit)}
          className={['button'].join(' ')}>
          {loading ? <ButtonLoader /> : t('COMMON.CONFIRM')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MakeSettleStatement;
